// Growem — central store. Auth + all day_state rows + meals/logs/chat, with optimistic
// mutations that persist to Supabase. Set maps (done/notes/weights/log) are jsonb → 1:1 port.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { type DayState, type Meal, type LogEntry, type ChatMsg, type Profile,
  type OnboardingPayload, defaultDay } from '../types'
import { dow, todayKey, planIndexForDow, hhmm } from '../domain/dates'
import { currentStreak, bestStreak, reorderSetData, parseSetKey, exComplete } from '../domain/rules'
import { GOALS, setUserPlanBundle, planForDate, prepForDate, cooldownForDate, type PlanBundle } from '../data/plan'
import { emptyStacks, pushEntry, undoStep, redoStep, type HistoryStacks } from '../domain/history'
// Sesión movible: la rutina efectiva de un día y las reglas para cambiarla viven en un único
// módulo puro (domain/planDay). El store no re-deriva el índice en ninguna línea propia: importa
// planIdxOf para que exista UNA sola fórmula de índice efectivo en todo el repo.
import { planIdxOf, planDayDecision, applyPlanDay, planDayLabel, type PlanDayDecision } from '../domain/planDay'
import { rowToDay, dayToRow } from './dayRow'

// ---------- row <-> model mapping ----------
// rowToDay/dayToRow viven en dayRow.ts (módulo puro, sin supabase) para que el round-trip de
// persistencia sea testeable en vitest: dayToRow es el único escritor de plan_day_index (D7).
type Row = Record<string, any>

function rowToMeal(r: Row): Meal {
  return {
    id: r.id, day: r.day, t: hhmm(new Date(r.logged_at)), n: r.name ?? '',
    kcal: r.kcal ?? 0, prot: r.protein_g ?? 0, carbs: r.carbs_g ?? undefined, fat: r.fat_g ?? undefined,
    photo: r.photo_path ? 1 : 0, photoPath: r.photo_path ?? null,
    source: r.source, note: r.coach_note ?? undefined,
  }
}

// Fallback identity for a profile whose name isn't set yet (pre-onboarding). Derived from the
// email local-part (sebastian.gil.pinzon@... -> "Sebastian") so we NEVER render another user's name.
function nameFromEmail(email?: string | null): string {
  const first = (email?.split('@')[0] ?? '').split(/[._+-]/)[0]
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : ''
}

// ---------- store shape ----------
interface StoreState {
  days: Record<string, DayState>
  mealsByDay: Record<string, Meal[]>
  chatByDay: Record<string, ChatMsg[]>
  logs: LogEntry[]
}
const EMPTY: StoreState = { days: {}, mealsByDay: {}, chatByDay: {}, logs: [] }

const DEFAULT_PROFILE: Profile = {
  displayName: '', avatarLetter: '·', bestStreak: 0,
  coachVoice: 'goggins', cupSize: GOALS.cupMl, timezone: 'America/Bogota',
  planStatus: 'ready',   // placeholder only; governs solely the prof===null path (row unreadable)
  sex: null, bodyweightKg: null, heightCm: null, dob: null, level: null,
}

interface StoreApi {
  ready: boolean
  user: User | null
  profile: Profile
  state: StoreState
  today: string
  // auth
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signUp: (email: string, password: string) => Promise<{ error?: string }>
  saveOnboarding: (payload: OnboardingPayload) => Promise<{ error?: string }>
  signOut: () => Promise<void>
  // reads
  getDay: (day: string) => DayState
  streakNow: () => number
  // day mutations
  toggleSet: (day: string, key: string) => void
  setField: (day: string, key: string, patch: { w?: number; r?: number; rir?: number; rest?: number; at?: number }) => void
  setNote: (day: string, key: string, txt: string) => void
  setTargetOverride: (day: string, wkey: string, txt: string) => void
  addSet: (day: string, key: string, seedWeight?: number) => void
  removeSet: (day: string, key: string) => void
  cascadeWeight: (day: string, keys: string[], val: number) => void
  setVariant: (day: string, planIndex: number, e: number, name: string | null) => void
  reorderSet: (day: string, planIndex: number, e: number, from: number, to: number, planSets: number) => void
  // undo/redo (day-snapshot history with specific labels; see domain/history.ts)
  undo: () => void
  redo: () => void
  undoInfo: () => { label: string; day: string } | null
  redoInfo: () => { label: string; day: string } | null
  restoreSignal: number   // bumps on every undo/redo restore (Sesion clears its input drafts on it)
  refreshPlanStatus: () => Promise<void>
  // Cambia la rutina de un día (sesión movible). Devuelve la decisión de forma SÍNCRONA para que la
  // pantalla pueda explicar por qué no se pudo (fuera de rango / el día ya tiene series hechas).
  setPlanDay: (day: string, idx: number | null) => PlanDayDecision
  addWater: (day: string) => void
  toggleCreatine: (day: string) => void
  addProt: (day: string, g: number) => void
  addKcal: (day: string, k: number) => void
  setSleep: (day: string, s: string) => void
  setWeight: (day: string, w: number) => void
  setTrainAt: (day: string, t: string) => void
  // meals / logs / chat
  addMeal: (day: string, meal: Omit<Meal, 'id' | 't'>, description?: string) => Promise<void>
  loadMeals: (day: string) => Promise<void>
  addLog: (day: string, icon: string, txt: string) => void
  loadChat: (day: string) => Promise<void>
  addChat: (day: string, who: 'u' | 'g', txt: string) => Promise<void>
}

const Ctx = createContext<StoreApi | null>(null)
export function useStore(): StoreApi {
  const v = useContext(Ctx)
  if (!v) throw new Error('useStore must be used within <StoreProvider>')
  return v
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(false)
  const [profile, setProfile] = useState<Profile>(DEFAULT_PROFILE)
  const [state, setState] = useState<StoreState>(EMPTY)
  const today = todayKey()

  const stateRef = useRef(state); stateRef.current = state
  const userRef = useRef(user); userRef.current = user
  const profileRef = useRef(profile); profileRef.current = profile
  const loadedUidRef = useRef<string | null>(null)
  const writeChain = useRef<Record<string, Promise<void>>>({})

  // ---------- auth wiring ----------
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => applySession(data.session))
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Ignore background token refreshes / user-metadata updates — they must NOT re-run loadAll
      // (which flips ready→false and flashes the splash, discarding in-memory draft state).
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') return
      applySession(session)
    })
    return () => sub.subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function applySession(session: Session | null) {
    const u = session?.user ?? null
    setUser(u)
    if (u) {
      if (loadedUidRef.current === u.id) return // already loaded this user (dedupe getSession + INITIAL_SESSION)
      loadedUidRef.current = u.id
      void loadAll(u.id, u.email)
    } else {
      loadedUidRef.current = null
      daysShadow.current = {}; histRef.current = emptyStacks(); setHistVersion((v) => v + 1) // no cross-user history
      setUserPlanBundle(null) // clear the per-user plan + restore default GOALS (no cross-user leakage)
      setState(EMPTY); setProfile(DEFAULT_PROFILE); setReady(true)
    }
  }

  async function loadAll(userId: string, email?: string | null) {
    setReady(false)
    const [{ data: prof }, { data: dayRows }, { data: logRows }, { data: planRow }] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
      supabase.from('day_state').select('*').order('day', { ascending: true }),
      supabase.from('activity_logs').select('*').order('logged_at', { ascending: false }).limit(200),
      // The user's generated plan (PLAN_GENERATION_METHODOLOGY). RLS scopes to the owner; a user with
      // no row (Sebastian, grandfathered) gets null -> the static built-in plan stays active.
      supabase.from('plans').select('data').eq('user_id', userId).eq('active', true)
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
    ])
    // Install the bundle BEFORE any state that renders plan-derived UI (and before setReady below);
    // setUserPlanBundle validates the shape and falls back to static on a malformed row.
    setUserPlanBundle((planRow?.data as PlanBundle | undefined) ?? null)
    if (prof) {
      const fb = nameFromEmail(email)
      setProfile({
        displayName: prof.display_name ?? fb,
        avatarLetter: prof.avatar_letter ?? (fb.charAt(0).toUpperCase() || '·'),
        bestStreak: prof.best_streak ?? 0, coachVoice: prof.coach_voice ?? 'goggins',
        cupSize: prof.cup_size_ml ?? GOALS.cupMl, timezone: prof.timezone ?? 'America/Bogota',
        planStatus: prof.plan_status ?? 'onboarding',   // fail-closed: a loaded row reading null -> the form
        // Intake fields for the Tier-2c estimate. select('*') already returns them; map straight through
        // (null when unset, e.g. a grandfathered account that never onboarded).
        sex: prof.sex ?? null,
        bodyweightKg: prof.weight_kg ?? null,
        heightCm: prof.height_cm ?? null,
        dob: prof.dob ?? null,
        level: prof.level ?? null,
      })
    }
    const days: Record<string, DayState> = {}
    for (const r of dayRows ?? []) days[r.day] = rowToDay(r)
    // Fresh session baseline: shadow mirrors the loaded days; history never crosses a (re)load.
    daysShadow.current = { ...days }
    histRef.current = emptyStacks()
    setHistVersion((v) => v + 1)
    const logs: LogEntry[] = (logRows ?? []).map((r) => ({
      id: r.id, day: r.day, t: hhmm(new Date(r.logged_at)), icon: r.icon ?? '', txt: r.txt,
    }))
    setState({ days, mealsByDay: {}, chatByDay: {}, logs })
    // Load today's meals + chat eagerly (the two screens that open on today).
    await Promise.all([loadMeals(today), loadChat(today)])
    setReady(true)
    // Ensure best streak reflects seeded history.
    void bumpBestStreak(days, userId)
  }

  // ---------- persistence primitive ----------
  // Serialize writes per day so rapid mutations (water/protein taps, set toggles) can't land
  // out of order and persist a stale full-row snapshot.
  const upsertDay = useCallback((d: DayState) => {
    const uid = userRef.current?.id
    if (!uid) return
    const prev = writeChain.current[d.day] ?? Promise.resolve()
    writeChain.current[d.day] = prev
      .then(async () => {
        const { error } = await supabase.from('day_state').upsert(dayToRow(d, uid), { onConflict: 'user_id,day' })
        if (error) console.error('day_state upsert', error.message)
      })
      .catch(() => {})
  }, [])

  // ---- undo/redo history (audited): synchronous day shadow so two same-tick mutations (e.g. a
  // toggle followed by its {at} stamp) never capture a stale baseline; history is OPT-IN per call
  // site (viewing a day or a meal macro-fold pushes nothing); no-op mutators (fn returns the same
  // reference) neither push nor persist. Labels may be functions of (prev, next) so the popup always
  // states the REAL applied delta (e.g. the actual cup size, the clamped protein change).
  const daysShadow = useRef<Record<string, DayState>>({})
  const histRef = useRef<HistoryStacks>(emptyStacks())
  const [histVersion, setHistVersion] = useState(0)
  const [restoreSignal, setRestoreSignal] = useState(0)
  void histVersion // state exists to re-render header buttons; readers use undoInfo()/redoInfo()

  type HistArg = { actionKey: string; label: string | ((prev: DayState, next: DayState) => string) }

  const mutateDay = useCallback((day: string, fn: (d: DayState) => DayState, persist = true, hist?: HistArg) => {
    // OJO (contrato con domain/planDay): defaultDay PRE-RELLENA planDayIndex con el índice del
    // weekday, así que un día sin fila nunca llega al dominio con planDayIndex null. Consecuencia
    // deliberada: elegir "la que toca hoy" en un día virgen es no-op (planDayDecision compara contra
    // el almacenado, que ya trae el weekday) y no persiste ni apila undo; la escritura explícita del
    // índice solo ocurre sobre filas cargadas de la DB con plan_day_index NULL. Si algún día se
    // quiere que ese caso "clave" la rutina de verdad, hay que sembrar aquí con planDayIndex null.
    const cur = daysShadow.current[day] ?? stateRef.current.days[day] ?? defaultDay(day, planIndexForDow(dow(day)))
    const nd = fn(cur)
    if (nd === cur) return // no-op: nothing happened, push nothing, persist nothing
    daysShadow.current[day] = nd
    if (hist) {
      const label = typeof hist.label === 'function' ? hist.label(cur, nd) : hist.label
      histRef.current = pushEntry(histRef.current, { day, label, actionKey: hist.actionKey, prev: cur }, Date.now())
      setHistVersion((v) => v + 1)
    }
    if (persist) void upsertDay(nd)
    setState((prev) => ({ ...prev, days: { ...prev.days, [day]: nd } }))
  }, [upsertDay])

  // Resolve a set-key to user vocabulary for labels ("S2 de Remo en polea baja", prep item names).
  const labelForKey = useCallback((day: string, key: string, d: DayState): string => {
    if (key.startsWith('prep-') || key.startsWith('post-')) {
      const i = Number(key.split('-')[1])
      // Índice EFECTIVO del día (el almacenado manda, el calendario es el respaldo). Antes esta
      // línea repetía la fórmula a mano; ahora la comparte con las pantallas vía planIdxOf, así el
      // nombre del paso de preparación/cierre que ve el usuario en el popup de deshacer sale
      // siempre de la MISMA rutina que se está pintando, incluso con la rutina movida.
      const pi = planIdxOf(d)
      const list = key.startsWith('prep-') ? prepForDate(day) : cooldownForDate(day)
      const item = pi != null ? list[pi]?.[i]?.label : undefined
      return `${key.startsWith('prep-') ? 'preparación' : 'cierre'}: ${item ?? 'paso'}`
    }
    const { d: pd, e, s, c } = parseSetKey(key)
    const ex = planForDate(day)[pd]?.ex[e]
    if (!ex) return `serie S${s + 1}`
    if (c !== -1) return `ronda ${s + 1} (${c === 0 ? 'A' : 'B'}) de ${ex.pair?.[c]?.n ?? ex.n}`
    const name = d.variants[`${pd}-${e}`] ?? ex.n
    return `S${s + 1} de ${name}`
  }, [])

  async function bumpBestStreak(days: Record<string, DayState>, userId: string) {
    const best = Math.max(bestStreak(days, today), profileRef.current.bestStreak)
    if (best > profileRef.current.bestStreak) {
      setProfile((p) => ({ ...p, bestStreak: best }))
      await supabase.from('profiles').update({ best_streak: best }).eq('id', userId)
    }
  }

  // ---------- day mutations ----------
  const getDay = useCallback((day: string): DayState => {
    return stateRef.current.days[day] ?? defaultDay(day, planIndexForDow(dow(day)))
  }, [])

  const streakNow = useCallback(() => currentStreak(stateRef.current.days, today), [today])

  const toggleSet = useCallback((day: string, key: string) => {
    mutateDay(day, (d) => {
      const done = { ...d.done }
      if (done[key]) delete done[key]; else done[key] = 1
      return { ...d, done }
    }, true, {
      actionKey: `toggle:${key}`,
      label: (prev, next) => {
        const on = !!next.done[key] && !prev.done[key]
        const what = labelForKey(day, key, next)
        if (key.startsWith('prep-') || key.startsWith('post-')) return `${what} ${on ? 'marcado' : 'desmarcado'}`
        // "ejercicio finalizado" when this toggle completes the plan's sets for that exercise.
        if (on && /^\d/.test(key)) {
          const { d: pd, e } = parseSetKey(key)
          const ex = planForDate(day)[pd]?.ex[e]
          if (ex && exComplete(next, pd, e, ex)) {
            const name = next.variants[`${pd}-${e}`] ?? ex.n
            return `ejercicio finalizado (${name})`
          }
        }
        return on ? `serie completada (${what})` : `serie desmarcada (${what})`
      },
    })
    const uid = userRef.current?.id
    if (uid) setTimeout(() => bumpBestStreak(stateRef.current.days, uid), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutateDay, labelForKey])

  const setField = useCallback((day: string, key: string, patch: { w?: number; r?: number; rir?: number; rest?: number; at?: number }) => {
    // {at}-only stamps push NO history: the completion timestamp travels with its toggle's snapshot
    // (the toggle entry was captured before this same-tick write), so undoing the toggle reverts both.
    const field = (['w', 'r', 'rir', 'rest'] as const).find((f) => patch[f] !== undefined || f in patch)
    const hist: HistArg | undefined = field ? {
      actionKey: `field:${key}:${field}`,
      label: (_prev, next) => {
        const noun = field === 'w' ? 'peso' : field === 'r' ? 'reps' : field === 'rir' ? 'RIR' : 'descanso'
        return field === 'rest'
          ? `descanso registrado (${labelForKey(day, key, next)})`
          : `editar ${noun} (${labelForKey(day, key, next)})`
      },
    } : undefined
    mutateDay(day, (d) => ({ ...d, log: { ...d.log, [key]: { ...d.log[key], ...patch } } }), true, hist)
  }, [mutateDay, labelForKey])

  const setNote = useCallback((day: string, key: string, txt: string) => {
    mutateDay(day, (d) => ({ ...d, notes: { ...d.notes, [key]: txt } }), true,
      { actionKey: `note:${key}`, label: (_p, n) => `nota de serie (${labelForKey(day, key, n)})` })
  }, [mutateDay, labelForKey])

  const setTargetOverride = useCallback((day: string, wkey: string, txt: string) => {
    mutateDay(day, (d) => ({ ...d, weights: { ...d.weights, [wkey]: txt } }), true,
      { actionKey: `target:${wkey}`, label: `peso objetivo editado (${txt})` })
  }, [mutateDay])

  // ---- extra sets (clone/add beyond the plan) + weight cascade-down ----
  // Add a set: write a persistent key so it survives reload and is counted by normalSetsCount. A
  // `seedWeight` (the prior set's load) pre-fills the new row ("clone the set"); otherwise an empty {}
  // sentinel. Idempotent.
  const addSet = useCallback((day: string, key: string, seedWeight?: number) => {
    mutateDay(day, (d) => ({
      ...d,
      log: { ...d.log, [key]: seedWeight != null ? { ...d.log[key], w: seedWeight } : (d.log[key] ?? {}) },
    }), true, { actionKey: `addset:${key}`, label: (_p, n) => `serie agregada (${labelForKey(day, key, n)})` })
  }, [mutateDay, labelForKey])

  // Remove a set entirely: drop its log entry + done flag. (The caller also clears its input drafts.)
  const removeSet = useCallback((day: string, key: string) => {
    mutateDay(day, (d) => {
      const log = { ...d.log }; delete log[key]
      const done = { ...d.done }; delete done[key]
      return { ...d, log, done }
    }, true, { actionKey: `rmset:${key}`, label: (p) => `serie quitada (${labelForKey(day, key, p)})` })
  }, [mutateDay, labelForKey])

  // Swap (or revert, name=null) an exercise's variant for ONE day. Audited state machine:
  //  - BLOCKED (returns unchanged) if any of today's sets for that slot is done OR carries typed
  //    numbers (w/r/rir). {at}/{rest} residue from a tap+untap never blocks. Extra sets included
  //    (the scan covers every normal key of the slot, not 0..sets-1). Re-checked INSIDE the
  //    functional update so a race with a set toggle can't slip through. Applies to revert too
  //    (otherwise two taps would re-attribute today's logged sets to the original name).
  //  - On ANY successful change (including revert) the positional target override "w{d}-{e}" is
  //    cleared: an override typed for one implement must never become another implement's target.
  const setVariant = useCallback((day: string, planIndex: number, e: number, name: string | null) => {
    mutateDay(day, (d) => {
      const prefix = `${planIndex}-${e}-`
      const hasData = (k: string) => {
        const l = d.log[k]
        return !!d.done[k] || l?.w != null || l?.r != null || l?.rir != null
      }
      const blocked = [...Object.keys(d.done), ...Object.keys(d.log)].some(
        (k) => k.startsWith(prefix) && k.split('-').length === 3 && hasData(k))
      if (blocked) return d
      const variants = { ...d.variants }
      if (name) variants[`${planIndex}-${e}`] = name; else delete variants[`${planIndex}-${e}`]
      const weights = { ...d.weights }
      delete weights[`w${planIndex}-${e}`]
      return { ...d, variants, weights }
    }, true, {
      actionKey: `variant:${planIndex}-${e}`,
      label: () => {
        const base = planForDate(day)[planIndex]?.ex[e]?.n ?? 'ejercicio'
        return name ? `cambio de ejercicio (${base} a ${name})` : `vuelta al ejercicio original (${base})`
      },
    })
  }, [mutateDay])

  // Reorder one exercise's sets for a day (pure payload permutation; see rules.reorderSetData).
  const reorderSet = useCallback((day: string, planIndex: number, e: number, from: number, to: number, planSets: number) => {
    mutateDay(day, (d) => reorderSetData(d, planIndex, e, from, to, planSets), true, {
      actionKey: `reorder:${planIndex}-${e}`,
      label: (_p, n) => `series reordenadas (${n.variants[`${planIndex}-${e}`] ?? planForDate(day)[planIndex]?.ex[e]?.n ?? 'ejercicio'})`,
    })
  }, [mutateDay])

  // Cascade a weight to a pre-computed set of BELOW keys (the caller derives them via cascadeWeightKeys
  // from fresh state, so only empty, not-done sets are targeted). Re-checks `done` inside the write as a
  // belt so a set completed between derive and write is never overwritten. Atomic single mutation.
  const cascadeWeight = useCallback((day: string, keys: string[], val: number) => {
    if (keys.length === 0) return
    mutateDay(day, (d) => {
      const log = { ...d.log }
      for (const k of keys) if (!d.done[k]) log[k] = { ...log[k], w: val }
      return { ...d, log }
    }, true, { actionKey: `cascade:${keys[0] ?? ''}`, label: `peso copiado a las series siguientes (${val} kg)` })
  }, [mutateDay])

  // ---- sesión movible: ÚNICO escritor de la rutina de un día ----
  // Sustituye al escritor anterior, que guardaba con persist=false: el cambio se veía en pantalla pero
  // no viajaba a la base, así que se perdía al recargar y encima se filtraba de rebote con la
  // siguiente mutación cualquiera (la fila entera se sube con dayToRow). Ahora persiste inmediato
  // (D7) y deja entrada de deshacer con nombre llano.
  //
  // Reglas repartidas así: planDayDecision dice SI se puede y si el cambio es real (rango, no-op,
  // día con series ya hechas) y applyPlanDay devuelve el MISMO objeto cuando no hay cambio, que es
  // justo el contrato de no-op de mutateDay (no persiste, no apila historial).
  const setPlanDay = useCallback((day: string, idx: number | null): PlanDayDecision => {
    // Se inicializa con un valor válido de la unión en vez de declararla sin asignar: TypeScript no
    // puede saber que mutateDay invoca el updater de forma síncrona y daría TS2454 ("used before
    // being assigned"). La ejecución síncrona sí está garantizada (mutateDay hace `const nd = fn(cur)`
    // antes de cualquier setState), por eso capturar la decisión en el closure y devolverla es fiable.
    // PERO el default por sí solo convertiría "el updater nunca corrió" en "todo bien, no cambió
    // nada" de forma silenciosa; `called` hace la invariante verificable: si un refactor futuro de
    // mutateDay mueve `fn` dentro del updater de setState o agrega un early-return, en DEV se grita.
    let res: PlanDayDecision = { ok: true, changed: false }
    let called = false
    mutateDay(day, (d) => {
      called = true
      // La decisión se RE-EVALÚA aquí dentro, contra el día más fresco (mismo blindaje de carrera
      // que setVariant): entre el toque y la escritura pudo marcarse una serie, y esa serie manda.
      res = planDayDecision(d, idx)
      return applyPlanDay(d, idx)
    }, true, {
      actionKey: `planday:${day}`,   // day-scoped y sin coalescer: A -> B -> A son tres entradas
      // La etiqueta del popup nombra la rutina EFECTIVA resultante (planIdxOf), no el campo crudo:
      // si algún día se guardara null, el popup diría lo mismo que sigue pintando la pantalla.
      label: (_p, n) => `rutina cambiada a ${planDayLabel(day, planIdxOf(n))}`,
    })
    // Guardia de DEV: si mutateDay dejó de invocar el updater síncronamente, `res` sería el default
    // y estaríamos anunciando éxito sin haber decidido nada (el bloqueo por series no habría corrido).
    if (!called && import.meta.env.DEV) {
      console.error('setPlanDay: mutateDay no invocó el updater síncronamente; la decisión devuelta es el default')
    }
    return res
  }, [mutateDay])

  const addWater = useCallback((day: string) => {
    const cup = profileRef.current.cupSize
    mutateDay(day, (d) => ({ ...d, water: d.water + cup }), true,
      { actionKey: 'water', label: (p, n) => `agua +${n.water - p.water} ml` })
  }, [mutateDay])

  const toggleCreatine = useCallback((day: string) => {
    mutateDay(day, (d) => ({ ...d, creatine: !d.creatine }), true,
      { actionKey: 'creatine', label: (_p, n) => (n.creatine ? 'creatina marcada' : 'creatina desmarcada') })
  }, [mutateDay])

  const addProt = useCallback((day: string, g: number) => {
    mutateDay(day, (d) => ({ ...d, prot: Math.max(0, d.prot + g) }), true,
      { actionKey: 'prot', label: (p, n) => `proteína ${n.prot - p.prot >= 0 ? '+' : ''}${n.prot - p.prot} g` })
  }, [mutateDay])

  const addKcal = useCallback((day: string, k: number) => {
    mutateDay(day, (d) => ({ ...d, kcal: Math.max(0, d.kcal + k) }), true,
      { actionKey: 'kcal', label: (p, n) => `calorías ${n.kcal - p.kcal >= 0 ? '+' : ''}${n.kcal - p.kcal}` })
  }, [mutateDay])

  const setSleep = useCallback((day: string, s: string) => {
    mutateDay(day, (d) => ({ ...d, sleep: s }), true, { actionKey: 'sleep', label: `sueño registrado (${s})` })
  }, [mutateDay])

  const setWeight = useCallback((day: string, w: number) => {
    mutateDay(day, (d) => ({ ...d, weight: w }), true, { actionKey: 'weight', label: `pesaje registrado (${w} kg)` })
  }, [mutateDay])

  const setTrainAt = useCallback((day: string, t: string) => {
    mutateDay(day, (d) => ({ ...d, trainAt: t }), true, { actionKey: 'trainat', label: `hora de entreno fijada (${t})` })
  }, [mutateDay])

  // ---------- logs ----------
  const addLog = useCallback((day: string, icon: string, txt: string) => {
    const uid = userRef.current?.id
    const entry: LogEntry = { day, t: hhmm(), icon, txt }
    setState((prev) => ({ ...prev, logs: [entry, ...prev.logs] }))
    // supabase-js builders are lazy — attach .then so the insert actually executes.
    if (uid) {
      supabase.from('activity_logs').insert({ user_id: uid, day, icon, txt })
        .then(({ error }) => { if (error) console.error('activity_logs insert', error.message) })
    }
  }, [])

  // ---------- meals ----------
  const loadMeals = useCallback(async (day: string) => {
    const { data } = await supabase.from('meals').select('*').eq('day', day).order('logged_at', { ascending: true })
    const meals = (data ?? []).map(rowToMeal)
    setState((prev) => ({ ...prev, mealsByDay: { ...prev.mealsByDay, [day]: meals } }))
  }, [])

  const addMeal = useCallback(async (day: string, meal: Omit<Meal, 'id' | 't'>, description?: string) => {
    const uid = userRef.current?.id
    if (!uid) return
    const { data, error } = await supabase.from('meals').insert({
      user_id: uid, day, name: meal.n, description: description ?? null,
      kcal: meal.kcal, protein_g: meal.prot, carbs_g: meal.carbs ?? null, fat_g: meal.fat ?? null,
      source: meal.source ?? 'manual', coach_note: meal.note ?? null, photo_path: meal.photoPath ?? null,
    }).select('*').maybeSingle()
    // Only fold macros / append / log when the row actually persisted — otherwise the day totals
    // would inflate against a meal that doesn't exist (unrecoverable divergence on reload).
    if (error || !data) { console.error('meal insert failed', error?.message); return }
    const saved = rowToMeal(data)
    setState((prev) => ({ ...prev, mealsByDay: { ...prev.mealsByDay, [day]: [...(prev.mealsByDay[day] ?? []), saved] } }))
    mutateDay(day, (d) => ({
      ...d, prot: d.prot + meal.prot, kcal: d.kcal + meal.kcal,
      carbs: d.carbs + (meal.carbs ?? 0), fat: d.fat + (meal.fat ?? 0),
    }))
    addLog(day, meal.photo ? '📷' : '🍽', `${meal.n} · ${meal.kcal} kcal · ${meal.prot} g prot`)
  }, [mutateDay, addLog])

  // ---------- chat ----------
  const loadChat = useCallback(async (day: string) => {
    const { data } = await supabase.from('coach_messages').select('*').eq('day', day).order('seq', { ascending: true })
    const msgs: ChatMsg[] = (data ?? []).map((r) => ({ who: r.role === 'coach' ? 'g' : 'u', txt: r.txt, seq: r.seq }))
    setState((prev) => ({ ...prev, chatByDay: { ...prev.chatByDay, [day]: msgs } }))
  }, [])

  const addChat = useCallback(async (day: string, who: 'u' | 'g', txt: string) => {
    const uid = userRef.current?.id
    const prevMsgs = stateRef.current.chatByDay[day] ?? []
    const seq = (prevMsgs[prevMsgs.length - 1]?.seq ?? -1) + 1
    const msg: ChatMsg = { who, txt, seq }
    setState((prev) => ({ ...prev, chatByDay: { ...prev.chatByDay, [day]: [...(prev.chatByDay[day] ?? []), msg] } }))
    if (uid) await supabase.from('coach_messages').insert({ user_id: uid, day, seq, role: who === 'g' ? 'coach' : 'user', txt })
  }, [])

  // ---------- auth actions ----------
  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error?.message }
  }, [])
  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password })
    return { error: error?.message }
  }, [])
  const saveOnboarding = useCallback(async (payload: OnboardingPayload): Promise<{ error?: string }> => {
    const uid = userRef.current?.id
    if (!uid) return { error: 'Sesión no encontrada. Vuelve a entrar.' }
    // Server-authoritative: writes typed intake + flips onboarding->pending; can never set 'ready'.
    const { error } = await supabase.rpc('submit_onboarding', { p: payload })
    if (error) return { error: error.message }
    const displayName = payload.nombre.trim() || 'Atleta'
    const avatarLetter = displayName.charAt(0).toUpperCase() || 'A'
    // Optimistic local flip (no reload -> no splash flash). Gate reads profile.planStatus -> re-renders
    // into <Waiting/>. Do NOT rely on USER_UPDATED (swallowed at store.tsx:126).
    setProfile((prev) => ({ ...prev, displayName, avatarLetter, planStatus: 'pending' }))
    return {}
  }, [])
  const signOut = useCallback(async () => { await supabase.auth.signOut() }, [])

  // ---- undo/redo: restore the snapshot through the normal persistence path, WITHOUT pushing
  // history for the restore itself; bump restoreSignal so Sesion clears its input drafts.
  // DEUDA CONOCIDA (auditoría Fase 4, junto a D7): el restore repone el día ENTERO, y las mutaciones
  // que no apilan historial (hoy: el fold de macros de addMeal) quedan fuera de la pila LIFO, así que
  // un undo posterior las revierte y persiste esa reversión. Es una propiedad preexistente del diseño
  // de snapshot por día, no de setPlanDay. Arreglos candidatos: que addMeal apile su propia entrada
  // ("comida registrada (...)"), o que applyRestore preserve los campos de macros/agua ajenos a la
  // acción deshecha. Registrado como backlog; no se toca en este milestone.
  const applyRestore = useCallback((day: string, restore: DayState) => {
    daysShadow.current[day] = restore
    void upsertDay(restore)
    setState((prev) => ({ ...prev, days: { ...prev.days, [day]: restore } }))
    setHistVersion((v) => v + 1)
    setRestoreSignal((v) => v + 1)
  }, [upsertDay])

  const currentOf = useCallback((day: string): DayState =>
    daysShadow.current[day] ?? stateRef.current.days[day] ?? defaultDay(day, planIndexForDow(dow(day))), [])

  const undo = useCallback(() => {
    const top = histRef.current.undo[histRef.current.undo.length - 1]
    if (!top) return
    const r = undoStep(histRef.current, currentOf(top.day))
    if (!r) return
    histRef.current = r.stacks
    applyRestore(r.day, r.restore)
  }, [applyRestore, currentOf])

  const redo = useCallback(() => {
    const top = histRef.current.redo[histRef.current.redo.length - 1]
    if (!top) return
    const r = redoStep(histRef.current, currentOf(top.day))
    if (!r) return
    histRef.current = r.stacks
    applyRestore(r.day, r.restore)
  }, [applyRestore, currentOf])

  const undoInfo = useCallback(() => {
    const t = histRef.current.undo[histRef.current.undo.length - 1]
    return t ? { label: t.label, day: t.day } : null
  }, [])
  const redoInfo = useCallback(() => {
    const t = histRef.current.redo[histRef.current.redo.length - 1]
    return t ? { label: t.label, day: t.day } : null
  }, [])

  // Waiting-screen poll: cheap re-read of plan_status only. When the team flips the user to 'ready'
  // (plan generated), run the FULL loadAll once so the plan bundle + fresh profile load together and
  // the App gate re-renders into the tabbed app. Avoids the blind-loadAll splash while still pending.
  const refreshPlanStatus = useCallback(async () => {
    const uid = userRef.current?.id
    if (!uid) return
    const { data } = await supabase.from('profiles').select('plan_status').eq('id', uid).maybeSingle()
    if (data?.plan_status === 'ready' && profileRef.current.planStatus !== 'ready') {
      await loadAll(uid, userRef.current?.email)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const api: StoreApi = {
    ready, user, profile, state, today,
    signIn, signUp, signOut, saveOnboarding,
    getDay, streakNow,
    toggleSet, setField, setNote, setTargetOverride, addSet, removeSet, cascadeWeight, setVariant, reorderSet, undo, redo, undoInfo, redoInfo, restoreSignal, setPlanDay, refreshPlanStatus,
    addWater, toggleCreatine, addProt, addKcal, setSleep, setWeight, setTrainAt,
    addMeal, loadMeals, addLog, loadChat, addChat,
  }
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}
